import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Panel, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { FinancialNav } from '../features/ledger/FinancialNav';
import { Icon } from '../components/Icon';
import { RunList } from '../features/disbursements/RunList';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/financial/disbursements/')({
  component: DisbursementsPage,
});

/**
 * Money going out — TRUST_ACCOUNTING.md §8.
 *
 * UNDER FINANCIAL RATHER THAN UNDER RECONCILE, and no longer a rail item of its own. Reconciling
 * is about proving what the bank has already done; this is about instructing it to do something,
 * and the two are the most different pair of tasks in the product even though they read the same
 * figures. Financial is where the balances a run pays out of are read, so that is where the run
 * is struck from — reached by the `payout` button in its top bar, which is the rail item's glyph
 * kept so the destination still looks like itself.
 *
 * STRIKING A RUN IS THE ONLY THING THIS PAGE CREATES, and it deliberately takes no input beyond
 * which trust account. There is no form for "who to pay" and no field for an amount: the machine
 * reads the ledger and produces the list, because a person who can type an amount is a person
 * whose typo nothing downstream would question. What a person can do is take a line off, on the
 * run itself, with a reason.
 */
function DisbursementsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState('');

  const runs = useQuery(
    trpc.disbursement.list.queryOptions(
      {},
      // At month end two people watch this list while a third works it. A run that has moved on
      // must stop looking approvable quickly.
      { staleTime: 5_000 },
    ),
  );

  // §5's notification queue. Read here rather than on the owner page because an unsent warning is
  // an agency-level failure, not one owner's — see `pendingNotifications` in the API.
  const pending = useQuery(trpc.disbursement.pendingNotifications.queryOptions());

  const propose = useMutation(
    trpc.disbursement.propose.mutationOptions({
      meta: { errorTitle: 'Could not strike a run' },
      onSuccess: (result) => {
        void queryClient.invalidateQueries({ queryKey: trpc.disbursement.list.queryKey() });
        void navigate({ to: '/financial/disbursements/$ref', params: { ref: result.ref } });
      },
    }),
  );

  const accounts = runs.data?.trustBankAccounts ?? [];

  /*
   * ONE LIVE RUN PER KIND, since `0063`. An owner run waiting on its second signature no longer
   * blocks the supplier batch — the two cannot spend the same figure, because an approved bill has
   * already debited its owners — so each button is disabled by its own open run and not the other's.
   */
  const isLive = (status: string) => ['proposed', 'reviewed', 'approved'].includes(status);
  const liveOwner = runs.data?.runs.find((run) => isLive(run.status) && run.kind === 'owner');
  const liveSupplier = runs.data?.runs.find((run) => isLive(run.status) && run.kind === 'supplier');

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Disbursements"
        subtitle="Paying owners and contractors out of the trust account"
        actions={
          <>
            {accounts.length > 1 ? (
              <Select
                aria-label="Trust account"
                value={accountId}
                onChange={(event) => {
                  setAccountId(event.target.value);
                }}
                className="w-56"
              >
                <option value="">Choose a trust account…</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </Select>
            ) : null}

            {/*
             * TWO BUTTONS, NOT A PICKER BESIDE ONE. Which kind of run this is decides what the
             * next screen contains, who may sign it and what the batch file says — it is not a
             * parameter of one action, it is two actions. A dropdown defaulted to "Owners" is
             * also the shape in which somebody strikes the wrong one on the 20th.
             */}
            <Button
              size="sm"
              disabled={propose.isPending || liveOwner !== undefined}
              onClick={() => {
                propose.mutate(
                  accountId ? { trustBankAccountId: accountId, kind: 'owner' } : { kind: 'owner' },
                );
              }}
            >
              {propose.isPending ? 'Working' : 'Pay owners'}
            </Button>

            <Button
              size="sm"
              disabled={propose.isPending || liveSupplier !== undefined}
              onClick={() => {
                propose.mutate(
                  accountId
                    ? { trustBankAccountId: accountId, kind: 'supplier' }
                    : { kind: 'supplier' },
                );
              }}
            >
              {propose.isPending ? 'Working' : 'Pay bills'}
            </Button>
          </>
        }
      />
      <FinancialNav />

      <div className="flex flex-col gap-4">
        {/*
         * ONE LIVE RUN PER KIND is enforced by a partial unique index, and saying so here is the
         * difference between a disabled button and a disabled button somebody files a support
         * ticket about. Each notice names the run that is blocking its own button.
         */}
        {liveOwner ? (
          <Panel className="flex items-start gap-3 p-4">
            <Icon name="clock" className="mt-0.5 shrink-0 text-ink-subtle" size={18} />
            <p className="text-[0.8125rem] text-ink-muted">
              <span className="font-semibold text-ink">{liveOwner.ref} is still open.</span> Finish
              or cancel it before striking another owner run — two at once would both work from the
              same balances and pay every owner twice.
            </p>
          </Panel>
        ) : null}

        {liveSupplier ? (
          <Panel className="flex items-start gap-3 p-4">
            <Icon name="clock" className="mt-0.5 shrink-0 text-ink-subtle" size={18} />
            <p className="text-[0.8125rem] text-ink-muted">
              <span className="font-semibold text-ink">{liveSupplier.ref} is still open.</span>{' '}
              Finish or cancel it before striking another bill run — an invoice already queued on it
              cannot be put on a second.
            </p>
          </Panel>
        ) : null}

        {pending.data && pending.data.count > 0 ? (
          /*
           * §5 says the previous email and phone are notified the moment an owner's bank account
           * changes. The obligation is recorded the instant the change is written and cannot be
           * suppressed; the SENDING is not built yet, so it is shown rather than left silent. An
           * unsent warning that nobody can see is the same as no warning at all.
           */
          <Panel className="flex items-start gap-3 p-4 ring-1 ring-warn/40">
            <Icon name="alert" className="mt-0.5 shrink-0 text-warn" size={18} />
            <p className="text-[0.8125rem] text-ink-muted">
              <span className="font-semibold text-ink">
                {pending.data.count} bank-account change{pending.data.count === 1 ? '' : 's'} still
                to be notified.
              </span>{' '}
              The contacts on record have not yet been told their payee account changed. Contact
              them directly until automatic delivery is switched on —{' '}
              {pending.data.items
                .slice(0, 3)
                .map((item) => item.payeeName)
                .join(', ')}
              {pending.data.count > 3 ? ` and ${String(pending.data.count - 3)} more` : ''}.
            </p>
          </Panel>
        ) : null}

        <RunList runs={runs.data} isPending={runs.isPending} />
      </div>
    </div>
  );
}
